import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import KanbanBoard from '../components/KanbanBoard';
import CardDetailModal from '../components/CardDetailModal';
import { Loader2 } from 'lucide-react';
import VideoHelpButton from '../components/VideoHelpButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MediaProductionBoards = () => {
  const navigate = useNavigate();
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [boardDataLoading, setBoardDataLoading] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [columns, setColumns] = useState([]);
  const [streams, setStreams] = useState([]);
  const [cards, setCards] = useState([]);
  const [error, setError] = useState(null);
  // Modal state - managed by this page, not KanbanBoard
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedColumnName, setSelectedColumnName] = useState(null);

  useEffect(() => {
    fetchBoards();
  }, []);

  useEffect(() => {
    if (selectedBoard) {
      fetchBoardData(selectedBoard.board_id);
    }
  }, [selectedBoard]);

  const fetchBoards = async () => {
    console.log('MediaProductionBoards: Fetching boards from', `${BACKEND_URL}/api/boards`);
    try {
      const response = await fetch(`${BACKEND_URL}/api/boards`, { credentials: 'include' });
      console.log('MediaProductionBoards: Response status', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('MediaProductionBoards: All boards received:', data);
        const boardTypes = data.map(b => b.board_type);
        console.log('MediaProductionBoards: Board types:', boardTypes);
        
        const mediaBoards = data.filter(b => b.board_type === 'media_production');
        console.log('MediaProductionBoards: Filtered media boards:', mediaBoards);
        
        setBoards(mediaBoards);
        if (mediaBoards.length > 0) {
          setSelectedBoard(mediaBoards[0]);
        } else {
          // Show detailed error with actual board types received
          const boardInfo = data.map(b => `${b.name} (type: ${b.board_type})`).join(', ');
          setError(`No media_production boards found. Received ${data.length} boards: ${boardInfo || 'none'}`);
        }
      } else {
        const errorText = await response.text();
        console.error('MediaProductionBoards: Failed to fetch boards:', response.status, errorText);
        setError(`Failed to fetch boards: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('MediaProductionBoards: Error fetching boards:', error);
      setError(`Network error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchBoardData = async (boardId) => {
    console.log('MediaProductionBoards: Fetching data for board', boardId);
    setBoardDataLoading(true);
    try {
      const [columnsRes, streamsRes, cardsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/boards/${boardId}/columns`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/boards/${boardId}/streams`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/boards/${boardId}/cards`, { credentials: 'include' })
      ]);

      console.log('MediaProductionBoards: Columns response status', columnsRes.status);
      
      if (columnsRes.ok) {
        const columnsData = await columnsRes.json();
        console.log('MediaProductionBoards: Columns received:', columnsData.length, 'columns');
        setColumns(columnsData);
      } else {
        console.error('MediaProductionBoards: Failed to fetch columns:', columnsRes.status);
      }
      if (streamsRes.ok) setStreams(await streamsRes.json());
      if (cardsRes.ok) setCards(await cardsRes.json());
    } catch (error) {
      console.error('MediaProductionBoards: Error fetching board data:', error);
    } finally {
      setBoardDataLoading(false);
    }
  };

  const handleCardMove = async (cardId, newColumnId) => {
    try {
      // Find the card to check if it's a linked card
      const card = cards.find(c => c.card_id === cardId);
      
      // Determine which field to update
      // If the card is a linked card (show_in_media_production and original board is different),
      // update media_production_column_id. Otherwise update column_id.
      const isLinkedCard = card?.show_in_media_production && card?.board_id !== selectedBoard?.board_id;
      
      const updatePayload = isLinkedCard 
        ? { media_production_column_id: newColumnId }
        : { column_id: newColumnId };
      
      const response = await fetch(`${BACKEND_URL}/api/cards/${cardId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updatePayload)
      });

      if (response.ok && selectedBoard) {
        fetchBoardData(selectedBoard.board_id);
      }
    } catch (error) {
      console.error('Failed to move card:', error);
    }
  };

  const handleRefresh = () => {
    if (selectedBoard) {
      fetchBoardData(selectedBoard.board_id);
    }
  };

  // Handle card selection - open modal managed by this page
  const handleCardSelect = (card, columnName) => {
    setSelectedCard(card);
    setSelectedColumnName(columnName);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setSelectedCard(null);
    setSelectedColumnName(null);
  };

  // Handle card update from modal - refresh data but keep modal open
  const handleCardUpdate = async () => {
    if (selectedBoard) {
      await fetchBoardData(selectedBoard.board_id);
      // Update selectedCard with fresh data from the new cards array
      // This happens automatically when cards state updates
    }
  };

  // Keep selectedCard in sync with updated cards data
  const currentCard = selectedCard 
    ? cards.find(c => c.card_id === selectedCard.card_id) || selectedCard
    : null;

  if (loading) {
    return (
      <AppLayout title="Media Production">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#A2FF00] animate-spin" />
          <span className="ml-2 text-[#A2FF00]">Loading boards...</span>
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout title="Media Production">
        <div className="text-center py-12">
          <p className="text-red-400 mb-4">{error}</p>
          <button 
            onClick={() => { setError(null); setLoading(true); fetchBoards(); }}
            className="bg-[#A2FF00] text-black px-4 py-2 rounded-sm hover:bg-[#8EFF00]"
          >
            Retry
          </button>
        </div>
      </AppLayout>
    );
  }

  if (boards.length === 0) {
    return (
      <AppLayout title="Media Production">
        <div className="text-center text-gray-400 py-12">
          <p>No media production boards found.</p>
          <p className="text-sm mt-2">Please check the browser console (F12) for debugging information.</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title={selectedBoard?.name || 'Media Production'}>
      {/* Board selector if multiple boards */}
      {boards.length > 1 && (
        <div className="mb-4">
          <select
            value={selectedBoard?.board_id || ''}
            onChange={(e) => {
              const board = boards.find(b => b.board_id === e.target.value);
              setSelectedBoard(board);
            }}
            className="bg-[#0A0A0A] border border-white/20 text-white rounded-sm px-4 py-2 focus:border-[#A2FF00]"
          >
            {boards.map(board => (
              <option key={board.board_id} value={board.board_id}>
                {board.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Loading state for board data */}
      {boardDataLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#A2FF00] animate-spin" />
          <span className="ml-2 text-[#A2FF00]">Loading board data...</span>
        </div>
      )}

      {/* Kanban Board */}
      {selectedBoard && !boardDataLoading && columns.length > 0 && (
        <KanbanBoard
          columns={columns}
          streams={streams}
          cards={cards}
          onCardMove={handleCardMove}
          onRefresh={handleRefresh}
          boardType={selectedBoard.board_type}
          onCardSelect={handleCardSelect}
          externalModalControl={true}
        />
      )}

      {/* Card Detail Modal - managed by this page */}
      {selectedCard && (
        <CardDetailModal
          card={currentCard}
          isOpen={!!selectedCard}
          onClose={handleCloseModal}
          onUpdate={handleCardUpdate}
          boardType={selectedBoard?.board_type}
          columnName={selectedColumnName}
          defaultTab="media"
        />
      )}

      {/* Empty state when board has no columns */}
      {selectedBoard && !boardDataLoading && columns.length === 0 && (
        <div className="text-center text-gray-400 py-12">
          <p>This board has no columns yet.</p>
          <p className="text-sm mt-2">An administrator can add columns from the Edit Media Workflow page.</p>
        </div>
      )}
    </AppLayout>
  );
};

export default MediaProductionBoards;
